import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AnalyticsAPI from '../../../../APIs/analyticsAPI';
import API from '../../../../context/API';
import { ProfitData } from '../types';

// Request parameters
export interface ProfitMarginsRequestParams {
  startDate?: string;
  endDate?: string;
  filter?: string;
  merchantId?: string;
  store?: string;
}

interface Store {
  id: string;
  name?: string;
  [key: string]: any;
}

interface ProfitMarginsState {
  profitData: ProfitData | null;
  stores: Store[];
  loading: boolean;
  storesLoading: boolean;
  error: string | null;
}

const initialState: ProfitMarginsState = {
  profitData: null,
  stores: [],
  loading: false,
  storesLoading: false,
  error: null,
};

// Async thunk to fetch profit margins data
export const fetchProfitMargins = createAsyncThunk<
  ProfitData,
  ProfitMarginsRequestParams,
  { rejectValue: string }
>('profitMargins/fetchProfitMargins', async (params, { rejectWithValue }) => {
  try {
    const response = await AnalyticsAPI.getProfitMargins(params);
    const data: ProfitData = response?.data || {};
    return data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch profit margins data'
    );
  }
});

// Async thunk to fetch stores
export const fetchStores = createAsyncThunk<
  Store[],
  void,
  { rejectValue: string }
>('profitMargins/fetchStores', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await API.get('/stores', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const stores = response.data.data || [];
    return stores;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch stores'
    );
  }
});

const profitMarginsSlice = createSlice({
  name: 'profitMargins',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearProfitData: (state) => {
      state.profitData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch profit margins data
      .addCase(fetchProfitMargins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfitMargins.fulfilled, (state, action) => {
        state.loading = false;
        state.profitData = action.payload;
      })
      .addCase(fetchProfitMargins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.profitData = null;
      })
      // Fetch stores
      .addCase(fetchStores.pending, (state) => {
        state.storesLoading = true;
        state.error = null;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.storesLoading = false;
        state.stores = action.payload;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.storesLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearProfitData } = profitMarginsSlice.actions;
export default profitMarginsSlice.reducer;
